/**
 * Utility functions for sorting exhibitor and product data
 * These functions provide reusable sorting logic across multiple components
 */

/**
 * Sort items by exhibitor/company name
 * @param {Array} items - Array of items to sort
 * @param {String} order - Sort order: 'asc' (A-Z) or 'desc' (Z-A)
 * @returns {Array} Sorted array
 */
export const sortByExhibitorName = (items, order = "asc") => {
  const sorted = [...items].sort((a, b) => {
    const nameA = (a.COMPANY_NAME || "").toLowerCase();
    const nameB = (b.COMPANY_NAME || "").toLowerCase();

    if (order === "asc") {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  return sorted;
};

/**
 * Sort items by booth number
 * @param {Array} items - Array of items to sort
 * @param {String} order - Sort order: 'asc' (Low to High) or 'desc' (High to Low)
 * @returns {Array} Sorted array
 */
export const sortByBoothNumber = (items, order = "asc") => {
  const sorted = [...items].sort((a, b) => {
    const boothA = parseFloat(a.BOOTHS) || 0;
    const boothB = parseFloat(b.BOOTHS) || 0;

    if (order === "asc") {
      return boothA - boothB;
    } else {
      return boothB - boothA;
    }
  });

  return sorted;
};

/**
 * Main sorting function that applies the selected sort type and order
 * Featured items are always placed first (for exhibitor components)
 * @param {Array} items - Array of items to sort
 * @param {String} sortBy - Sort field: 'exhibitor_name' or 'booth_number'
 * @param {String} sortOrder - Sort order: 'asc' or 'desc'
 * @param {Boolean} prioritizeFeatured - Whether to place featured items first (default: false)
 * @returns {Array} Sorted array
 */
export const applySorting = (
  items,
  sortBy = "exhibitor_name",
  sortOrder = "asc",
  prioritizeFeatured = false
) => {
  if (!items || items.length === 0) {
    return [];
  }

  // Partition featured/non-featured once, then sort each; avoids sort-then-filter passes.
  if (prioritizeFeatured) {
    const featuredItems = [];
    const nonFeaturedItems = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.FEATURED === true) {
        featuredItems.push(item);
      } else {
        nonFeaturedItems.push(item);
      }
    }

    const sortFn =
      sortBy === "booth_number" ? sortByBoothNumber : sortByExhibitorName;
    const sortedFeatured = sortFn(featuredItems, sortOrder);
    const sortedNonFeatured = sortFn(nonFeaturedItems, sortOrder);

    return [...sortedFeatured, ...sortedNonFeatured];
  }

  let sortedItems = [];
  if (sortBy === "exhibitor_name") {
    sortedItems = sortByExhibitorName(items, sortOrder);
  } else if (sortBy === "booth_number") {
    sortedItems = sortByBoothNumber(items, sortOrder);
  } else {
    sortedItems = sortByExhibitorName(items, sortOrder);
  }

  return sortedItems;
};
